import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/auth";
import { api } from "@/api";
import { Spinner } from "@/components/ui-kit";
import Landing from "@/pages/Landing";
import RoleSelect from "@/pages/RoleSelect";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import GoogleRoleSelect from "@/pages/GoogleRoleSelect";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import CreatorDashboard from "@/pages/dashboard/CreatorDashboard";
import BusinessDashboard from "@/pages/dashboard/BusinessDashboard";
import Campaigns from "@/pages/dashboard/Campaigns";
import MyCampaigns from "@/pages/dashboard/MyCampaigns";
import PublishCampaign from "@/pages/dashboard/PublishCampaign";
import PublishAffiliate from "@/pages/dashboard/PublishAffiliate";
import CreatorsList from "@/pages/dashboard/CreatorsList";
import CreatorProfile from "@/pages/dashboard/CreatorProfile";
import CampaignDetail from "@/pages/dashboard/CampaignDetail";
import ApplicationDetail from "@/pages/dashboard/ApplicationDetail";
import CreatorApplications from "@/pages/dashboard/CreatorApplications";
import CreatorApplicationDetail from "@/pages/dashboard/CreatorApplicationDetail";
import BusinessApplications from "@/pages/dashboard/BusinessApplications";
import Messages from "@/pages/dashboard/Messages";
import Profile from "@/pages/dashboard/Profile";
import Settings from "@/pages/dashboard/Settings";
import Subscription from "@/pages/dashboard/Subscription";
import InfoPage from "@/pages/public/InfoPage";
import AdminDashboard from "@/pages/AdminDashboard";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } });
        setUser(data);
        window.history.replaceState(null, "", "/");
        navigate(data.role ? "/dashboard" : "/valitse-rooli-google", { replace: true });
      } catch {
        navigate("/kirjaudu", { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return <Spinner />;
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Spinner />;
  if (!user) return <Navigate to="/kirjaudu" replace />;
  if (!user.role) return <Navigate to="/valitse-rooli-google" replace />;
  if (user.role === "admin" && role !== "admin") return <Navigate to="/admin" replace />;
  if (role && user.role !== role) return <Navigate to="/dashboard" replace />;
  return children;
}

function DashboardHome() {
  const { user } = useAuth();
  return user.role === "creator" ? <CreatorDashboard /> : <BusinessDashboard />;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/valitse-rooli" element={<RoleSelect />} />
      <Route path="/valitse-rooli-google" element={<GoogleRoleSelect />} />
      <Route path="/kirjaudu" element={<Login />} />
      <Route path="/rekisteroidy" element={<Register />} />
      <Route path="/unohtui-salasana" element={<ForgotPassword />} />
      <Route path="/palauta-salasana" element={<ResetPassword />} />
      <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
      <Route path="/dashboard" element={<Protected><DashboardLayout /></Protected>}>
        <Route index element={<DashboardHome />} />
        <Route path="kampanjat" element={<Protected role="creator"><Campaigns /></Protected>} />
        <Route path="kampanjat/:id" element={<Protected role="creator"><CampaignDetail /></Protected>} />
        <Route path="hakemukset" element={<Protected role="creator"><CreatorApplications /></Protected>} />
        <Route path="hakemukset/:id" element={<Protected role="creator"><CreatorApplicationDetail /></Protected>} />
        <Route path="sisallontuottajat" element={<Protected role="business"><CreatorsList /></Protected>} />
        <Route path="sisallontuottajat/:id" element={<Protected role="business"><CreatorProfile /></Protected>} />
        <Route path="julkaise" element={<Protected role="business"><PublishCampaign /></Protected>} />
        <Route path="julkaise-affiliate" element={<Protected role="business"><PublishAffiliate /></Protected>} />
        <Route path="omat-kampanjat" element={<Protected role="business"><MyCampaigns /></Protected>} />
        <Route path="saapuneet-hakemukset" element={<Protected role="business"><BusinessApplications /></Protected>} />
        <Route path="saapuneet-hakemukset/:id" element={<Protected role="business"><ApplicationDetail /></Protected>} />
        <Route path="viestit" element={<Messages />} />
        <Route path="tilaus" element={<Subscription />} />
        <Route path="profiili" element={<Profile />} />
        <Route path="asetukset" element={<Settings />} />
      </Route>
      <Route path="/:slug" element={<InfoPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
